import { Dropdown } from 'antd'
import { ChangeEvent, ReactNode, useEffect, useRef, useState } from 'react'
import styles from './DropdownSelector.module.less'
import AIcon from '@/components/A/AIcon/AIcon'
import cls from 'classnames'
import AInput from '@/components/A/AInput/AInput'
import { useClickAway } from 'ahooks'


interface Opt {
  label: string, value: string
}

interface Props {
  options: Opt[]
  testId?: string
  placeholder?: string
  otherPlaceholder?: string
  onChange?: (p: Opt) => void
  onBlur?: () => void
  className?: string
  menuClassName?: string
  showSearch?: {
    filter?: () => boolean,
    render?: () => ReactNode
  } | boolean
}

function DropdownSelector(props: Props) {
  const [selected, setSelected] = useState<{ label: string, value: string } | undefined>(undefined)
  const [otherValue, setOtherValue] = useState('')
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const labelRef = useRef<HTMLDivElement>(null)
  const rawOptions = props.options
  const [options, setOptions] = useState(props.options)
  const [searchValue, setSearchValue] = useState('')
  const [searchInputFocus, setSearchInputFocus] = useState(false)

  const defaultFilter = (inputValue: string, label: string) => {
    return label.toLowerCase().includes(inputValue.toLowerCase())
  }
  const defaultRender = (inputValue: string, label: string) => {
    return <span key={inputValue + label}>{label}</span>
  }

  const handleSelect = (opt: Opt) => {
    setOtherValue('')
    setSearchValue('')
    setSelected(opt)
    setOpen(false)
  }

  useClickAway(() => {
    setOpen(false)
    props.onBlur?.()
    setSearchValue('')
  }, [menuRef, labelRef])

  useEffect(() => {
    const filterFn = typeof props.showSearch === 'object' ? props.showSearch.filter || defaultFilter : defaultFilter
    if (searchValue === '') {
      setOptions(rawOptions)
    } else {
      const filterOptions = rawOptions.filter(opt => opt.value !== 'Other').filter(opt => {
        return filterFn(searchValue, opt.label)
      })
      setOptions(filterOptions)
    }
  }, [searchValue, rawOptions])

  useEffect(() => {
    if (selected) {
      if (selected.value === 'Other') {
        props.onChange?.({ label: otherValue, value: 'Other' })
      } else {
        props.onChange?.(selected)
      }
    }
  }, [selected,])

  const menu = (
    <div className={cls(styles.menu, props.menuClassName)} ref={menuRef} data-testid={props.testId ? `${props.testId}-menu` : undefined}>
      {options.map(option => {
        if (option.value !== 'Other') {
          return <div
            key={option.value}
            className={cls(styles.menuItem, {
              [styles.selected]: selected?.value === option.value,
            })}
            data-testid={props.testId ? `${props.testId}-option-${option.value}` : undefined}
            onClick={() => {
              handleSelect(option)
            }}
          >
            <span className={styles.menuItemLabel}>
              {
                props.showSearch ? defaultRender(searchValue, option.label) : option.label
              }
            </span>
            {
              selected?.value === option.value &&
              <AIcon className={styles.selectIcon} type='icon-line-global_check' />
            }
          </div>
        } else {
          return <div key={option.value} className={cls(styles.menuItem, styles.otherItem, {
            [styles.selected]: selected?.value === option.value,
          })}>
            <span>{option.label}:</span>
            <AInput className={cls(styles.menuInput, 'menu-input')} placeholder={props.otherPlaceholder}
              testId={props.testId ? `${props.testId}-other-input` : undefined}
              value={otherValue} onChange={e => {
                setOtherValue(e.target.value)
                if (e.target.value) {
                  setSelected({ label: e.target.value, value: 'Other' })
                } else {
                  setSelected(undefined)
                }
              }}
            />
          </div>
        }
      })}
    </div>
  )

  const handleSearchInputFocus = () => {
    setSearchInputFocus(true)
  }
  const handleSearchInputBlur = () => {
    setSearchInputFocus(false)
  }
  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchValue(value)
  }

  return (
    <Dropdown overlay={menu} trigger={['click']} open={open} overlayClassName={props.testId ? `${props.testId}-dropdown` : undefined}>
      <div className={cls(styles.dropdownSelector, props.className)} ref={labelRef} data-testid={props.testId}
        onClick={() => {
          setOpen((o) => !o)
        }}>
        <div className={styles.dropdownLabelContent}>
          {
            props.showSearch &&
            <span className={styles.searchInput}>
              <input type="text"
                data-testid={props.testId ? `${props.testId}-search-input` : undefined}
                value={searchValue}
                onFocus={handleSearchInputFocus}
                onBlur={handleSearchInputBlur}
                onChange={handleSearchInputChange}
              />
            </span>
          }
          {
            !searchValue && (
              selected
                ? <div className={cls(styles.dropdownSelectorText, searchInputFocus && styles.searchInputFocus)}>
                  <span>
                    {selected.value === 'Other' ? otherValue : selected.label}
                  </span>
                </div>
                : <span className={styles.placeholder}>{props.placeholder || 'Please select'}</span>
            )
          }
        </div>
        <AIcon className={cls(styles.downIcon, 'down-icon')} type='icon-line-global_down'></AIcon>
      </div>
    </Dropdown>
  )
}

export default DropdownSelector
